import uuidService from '@/services/uuid.service';
import React from 'react';
import { Temporal } from 'temporal-polyfill';
import { ITimeZone } from '../types/ITimeZone';

interface IRange {
	start: Temporal.ZonedDateTime;
	end: Temporal.ZonedDateTime;
}

interface IArgs {
	timeZone: ITimeZone;
	onRangeSelected: (range: IRange) => void;
}

export function useSelectInterval(args: IArgs) {
	const [state, setState] = React.useState({
		newEvent: null as null | IRange,
		lastCross: null as null | 'below' | 'above',
	});

	function handleRangeChanged(args: { start: Temporal.PlainDate; end: Temporal.PlainDate }) {
		setState((s) => ({ ...s, from: args.start, to: args.end }));
	}

	function handleCalendarPointerUp() {
		if (!state.newEvent) {
			return;
		}

		args.onRangeSelected(state.newEvent);
		setState((s) => ({ ...s, lastCross: null, newEvent: null }));
	}

	function handleCalendarPointerDown(plainStart: Temporal.PlainDateTime) {
		const start = plainStart.round({ smallestUnit: 'minute', roundingIncrement: 30 }).toZonedDateTime(args.timeZone);
		const end = start.add({ minutes: 30 });

		setState((s) => ({
			...s,
			newEvent: { id: uuidService.v4(), start, end },
			lastCross: null,
		}));
	}

	/**
	 * This function handles the movement of the calendar pointer to adjust the event's start and end times.
	 * The logic is as follows:
	 * - If the pointer is moved after the current end time, adjust the end time.
	 * - If the pointer is moved before the current start time, adjust the start time.
	 * - If the pointer is moved between the start and end times:
	 *   - Adjust the end time if the last crossing was from below (pointer moved up).
	 *   - Adjust the start time if the last crossing was from above (pointer moved down).
	 */
	function handleCalendarPointerMove(plainStart: Temporal.PlainDateTime) {
		if (!state.newEvent) {
			return;
		}

		const newTime = plainStart.round({ smallestUnit: 'minute', roundingIncrement: 30 }).toZonedDateTime(args.timeZone);
		const { newEvent, lastCross } = state;
		const { start, end } = newEvent;

		// Case: The user moves the pointer after the end time
		if (newTime.epochMilliseconds > end.epochMilliseconds) {
			return setState((s) => ({ ...s, newEvent: { ...newEvent, end: newTime }, lastCross: 'below' }));
		}

		// Case: The user moves the pointer before the start time
		if (newTime.epochMilliseconds < start.epochMilliseconds) {
			return setState((s) => ({ ...s, newEvent: { ...newEvent, start: newTime }, lastCross: 'above' }));
		}

		// Case: The user moves the pointer between the start and end times
		if (lastCross === 'below') {
			// If coming from below, adjust the end time
			return setState((s) => ({ ...s, newEvent: { ...newEvent, end: newTime } }));
		}

		if (lastCross === 'above') {
			// If coming from above, adjust the start time
			return setState((s) => ({ ...s, newEvent: { ...newEvent, start: newTime } }));
		}
	}

	return {
		highlight: state.newEvent,
		handleCalendarPointerDown,
		handleCalendarPointerMove,
		handleCalendarPointerUp,
		handleRangeChanged,
	};
}
